export type ListingStatusBadgeVariant =
  | 'pending'
  | 'contingent'
  | 'fresh'
  | 'neutral';

export interface ListingStatusBadge {
  label: string;
  variant: ListingStatusBadgeVariant;
  mlsStatus: string;
}

/** Active-category MLS statuses that should not show a buyer-facing badge. */
const STATUSES_WITHOUT_BADGE = new Set([
  'ACTIVE',
  'ACTIVE-EXCLUSIVE AGENCY',
  'ACTIVE-LIMITED SERVICE LI',
]);

const STATUS_BADGES: Record<string, ListingStatusBadge> = {
  'OPTION-SHOW': { label: 'Pending', variant: 'pending', mlsStatus: 'OPTION-SHOW' },
  'ACTIVE-CONTINGENT': { label: 'Contingent', variant: 'contingent', mlsStatus: 'ACTIVE-CONTINGENT' },
  "RENTAL APPLICATION REC'D": {
    label: 'Application',
    variant: 'pending',
    mlsStatus: "RENTAL APPLICATION REC'D",
  },
  'BACK ON MARKET': { label: 'Back on market', variant: 'fresh', mlsStatus: 'BACK ON MARKET' },
  EXTENDED: { label: 'Extended', variant: 'neutral', mlsStatus: 'EXTENDED' },
  'FEASIBILITY PERIOD': { label: 'Contingent', variant: 'contingent', mlsStatus: 'FEASIBILITY PERIOD' },
  'OFFER ACKNOWLEDGED BID': {
    label: 'Pending',
    variant: 'pending',
    mlsStatus: 'OFFER ACKNOWLEDGED BID',
  },
  SOLD: { label: 'Sold', variant: 'neutral', mlsStatus: 'SOLD' },
  'SOLD NON MLS': { label: 'Sold', variant: 'neutral', mlsStatus: 'SOLD NON MLS' },
};

export type ListingStatusFilterKey =
  | 'active'
  | 'contingent'
  | 'pending'
  | 'back-on-market'
  | 'extended'
  | 'other';

export function normalizeMlsStatus(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

/** Maps RETS L_Status values to featured-properties filter keys. */
export function resolveListingStatusFilterKey(value: unknown): ListingStatusFilterKey | null {
  const status = normalizeMlsStatus(value);
  if (!status) {
    return null;
  }

  if (STATUSES_WITHOUT_BADGE.has(status)) {
    return 'active';
  }

  const badge = STATUS_BADGES[status];
  if (badge) {
    if (badge.variant === 'pending') {
      return 'pending';
    }
    if (badge.variant === 'contingent') {
      return 'contingent';
    }
    if (status === 'BACK ON MARKET') {
      return 'back-on-market';
    }
    if (status === 'EXTENDED') {
      return 'extended';
    }
  }

  if (status === 'PENDING' || status.includes('PENDING')) {
    return 'pending';
  }
  if (status.includes('CONTINGENT')) {
    return 'contingent';
  }

  return 'other';
}

export function resolveListingStatusBadge(property: Record<string, unknown>): ListingStatusBadge | null {
  const status = normalizeMlsStatus(property['Status']);
  if (!status || STATUSES_WITHOUT_BADGE.has(status)) {
    return null;
  }

  return STATUS_BADGES[status] ?? {
    label: formatFallbackStatusLabel(status),
    variant: 'neutral',
    mlsStatus: status,
  };
}

function formatFallbackStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function listingStatusBadgeClassNames(badge: ListingStatusBadge): string {
  return `listing-status-badge listing-status-badge--${badge.variant}`;
}

export function createListingStatusBadgeElement(badge: ListingStatusBadge): HTMLElement {
  const element = document.createElement('span');
  element.className = listingStatusBadgeClassNames(badge);
  element.textContent = badge.label;
  return element;
}

export function appendListingStatusBadge(
  imageSection: HTMLElement,
  property: Record<string, unknown>,
): ListingStatusBadge | null {
  const badge = resolveListingStatusBadge(property);
  if (!badge) {
    return null;
  }

  imageSection.appendChild(createListingStatusBadgeElement(badge));
  return badge;
}
